<template>
  <section class="relative">
    <div class="absolute inset-0">
      <nuxt-img 
        class="w-full h-full md-max:object-cover"
        width="1024"
        height="787"
        loading="lazy"
        format="webp"
        sizes="xs:340px sm:440px md:768px lg:1024px xl:1024px 2xl:1024px"
        :src="backgroundImageSrc" 
        :alt="backgroundImageAlt" 
      />
    </div>
    <div class="relative z-10 gap-8 items-center px-4 py-8 mx-auto max-w-screen-xl xl:my-8 lg:grid lg:grid-cols-2 xl:gap-16 lg:py-8 lg:px-6 bg-glassmorhpism">
      <div class="sm:text-lg">
        <h2 class="mb-4 text-center h2 md:text-left" v-html="mainHeading"></h2>
        <div class="grid gap-8 pt-8 border-t border-brownLatte lg:grid-cols-1 sm:grid-cols-2">
          <div v-for="(item, index) in items" :key="index" class="flex">
            <div class="flex justify-center items-center mr-4 w-12 h-12 rounded shadow shrink-0">
              <nuxt-img 
                class="w-7 h-7 text-gray-900"
                width="28"
                height="28"
                loading="lazy"
                format="webp"
                :src="item.iconSrc" 
                :alt="item.iconAlt" 
              />
            </div>
            <div>
              <h3 class="mb-1 font-bold h4" v-html="item.heading"></h3>
              <h4 class="mb-1 font-semibold textp" v-html="item.subheading"></h4>
              <div class="font-normal textp" v-html="item.description"></div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div class="pt-6 lg:pt-0">
          <nuxt-img 
            class="mx-auto w-full max-w-xl lg:px-20 lg:max-w-auto"
            width="484"
            height="398"
            loading="lazy"
            format="webp"
            sizes="xs:358px sm:408px md:736px lg:472px xl:560px 2xl:484px"
            :src="productImageSrc" 
            :alt="productImageAlt" 
          />
        </div>
        <div class="flex relative flex-col p-6 rounded-lg xl:p-8">
          <div class="flex justify-center">
                      <h3 class="text-3xl sm:text-4xl font-ww-medium">Total is:</h3>
                   
                  </div>
          <div class="flex justify-center items-center">
            <div>
              <div class="flex flex-col gap-x-2 items-center sm:flex-row">
                <div class="text-2xl font-extrabold line-through text-lifeboost-red lg:text-2xl" v-html="oldPrice"></div>
                <div class="text-4xl font-extrabold !leading-none text-gray-900 lg:text-4xl" v-html="newPrice"></div>
              </div>
              <div class="flex justify-center">
                <div class="text-2xl font-extrabold !leading-none text-lifeboost-green-dark lg:text-2xl xl:text-2xl" v-html="discount"></div>
              </div>
            </div>
          </div>
          <button @click="addToCart()" class="px-5 py-2.5 my-5 text-sm font-bold text-center text-white uppercase rounded-lg bg-brownLatte font-ww-medium hover:opacity-80 focus:ring-4 focus:ring-primary-200 lg:text-xl lg:my-4" v-html="ctaText"></button>
          <div class="justify-between space-y-4 sm:space-y-0 sm:flex">
            <ul role="list" class="space-y-4">
              <li v-for="(feature, index) in features" :key="index" class="flex space-x-2.5">
                <svg class="flex-shrink-0 w-6 h-6 lg:mt-1 sm:mt-0.5 text-lifeboost-green-dark" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>
                <span class="text-base text-black sm:text-lg font-open-sans" v-html="feature.text"></span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>

const props = defineProps({
  backgroundImageSrc: {
    type: String,
    default: 'assets/images/bg-latte.png'
  },
  backgroundImageAlt: {
    type: String,
    default: 'background section'
  },
  mainHeading: {
    type: String,
    default: 'Step Inside the Lifeboost Latte Bundle'
  },
  items: {
    type: Array,
    default: () => [
      {
        iconSrc: 'assets/images/latte-icon-1.svg',
        iconAlt: 'icon 1',
        heading: '1 bag of Cognition Mushroom Coffee',
        subheading: '(30 day supply)  ($29.95)',
        description: 'Experience clarity and focus like never before, enriching your mornings with every cup.'
      },
      {
        iconSrc: 'assets/images/latte-icon-2.svg',
        iconAlt: 'icon 2',
        heading: '2 bags of Mitocreamer',
        subheading: '(30 day supply) ($39.99 per bag)',
        description: 'Indulge in creamy, wholesome goodness that fuels both body and spirit.'
      },
      {
        iconSrc: 'assets/images/latte-icon-3.svg',
        iconAlt: 'icon 3',
        heading: '1 free bottle of Everlo Electrolyte Drops',
        subheading: '($26.96 per bottle)',
        description: "Stay hydrated and energized, ensuring you're ready to tackle the day ahead."
      },
      {
        iconSrc: 'assets/images/latte-icon-4.svg',
        iconAlt: 'icon 4',
        heading: '1 free bottle of Vitamin D3/K2 Drops',
        subheading: '($26.96 per bottle)',
        description: "Feel the warmth of the sun in every sip, uplifting your body's health and your mood."
      },
      {
        iconSrc: 'assets/images/latte-icon-5.svg',
        iconAlt: 'icon 5',
        heading: 'Exclusive Bonuses for Early Bird Lifeboosters',
        description: 'The first 300 challengers receive bonus gifts: <span class="font-semibold underline text-brownLatte">our signature frother</span> and Double.'
      }
    ]
  },
  productImageSrc: {
    type: String,
    default: 'assets/images/image3-1.png'
  },
  productImageAlt: {
    type: String,
    default: 'coffee products'
  },
  oldPrice: {
    type: String,
    default: '$168.39'
  },
  newPrice: {
    type: String,
    default: '$79.95'
  },
  discount: {
    type: String,
    default: 'Save over 52%'
  },
  ctaText: {
    type: String,
    default: 'Join the Challenge Today'
  },
  features: {
    type: Array,
    default: () => [
      {
        text: 'Love It or Your Money Back'
      },
      {
        text: 'Discover a new zenith of well-being within 15 days, or get a full refund.'
      },
      {
        text: 'Refill ships in 30 days, 5 Days before your next shipment is set to go through, we will email you a reminder. By default, it will be another 30 servings delivered to your door. You can adjust or cancel at any time. '
      }
    ]
  },
  products: {
    type: Array,
    default: () => [
      { id: '41895677100092', qty: 1, price: 70.95, title: 'Latte Bundle', selling_plan: '1406730300' },
      { id: '40267005919292', qty: 1, price: 29.95, title: 'EverLo Coffee Drops', selling_plan: null },
      { id: '40282477330492', qty: 1, price: 0, title: 'Lifeboost Frother', selling_plan: null },
    ]
  },
  productHandle: {
    type: String,
    default: 'longevity-latte'
  },
  sellingPlanName: {
    type: String,
    default: '1 month subscription'
  }
})

const DYNAMIC_SELLING_PLAN_MARKERS = new Set(['1406730300', 'dynamic', 'auto']);

function isDynamicSellingPlanMarker(value) {
  return DYNAMIC_SELLING_PLAN_MARKERS.has(String(value));
}

function resolveProductSellingPlan(sellingPlan, resolvedSellingPlanId) {
  if (!sellingPlan) return null;
  if (isDynamicSellingPlanMarker(sellingPlan)) return resolvedSellingPlanId;
  return sellingPlan;
}

const sellingPlanId = await getSellingPlan(props.productHandle, props.sellingPlanName);

const cart = useCart();

function addToCart() {
  props.products.forEach(product => {
    cart.add({
      ...product,
      selling_plan: resolveProductSellingPlan(product.selling_plan, sellingPlanId),
    })
  })

  cart.checkout("?discount=CHALLENGE15DAYS")
}

defineBuilderComponent({
    friendlyName: "Description With List Pricing",
    inputs: [
    {
      name: 'backgroundImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'svg', 'png', 'webp'],
      defaultValue: 'assets/images/bg-latte.png'
    },
    {
      name: 'backgroundImageAlt',
      type: 'text',
      defaultValue: 'background section'
    },
    {
      name: 'mainHeading',
      type: 'richText',
      defaultValue: 'Step Inside the Lifeboost Latte Bundle'
    },
    {
      name: 'items',
      type: 'list',
      subFields: [
        {
          name: 'iconSrc',
          type: 'file',
          allowedFileTypes: ['svg', 'webp'],
          required: true
        },
        {
          name: 'iconAlt',
          type: 'text',
          required: true
        },
        {
          name: 'heading',
          type: 'richText',
          required: true
        },
        {
          name: 'subheading',
          type: 'richText',
          required: true
        },
        {
          name: 'description',
          type: 'richText',
          required: true
        }
      ],
      defaultValue: [
        {
          iconSrc: 'assets/images/latte-icon-1.svg',
          iconAlt: 'icon 1',
          heading: '1 bag of Cognition Mushroom Coffee',
          subheading: '(30 day supply)  ($29.95)',
          description: 'Experience clarity and focus like never before, enriching your mornings with every cup.'
        },
        {
          iconSrc: 'assets/images/latte-icon-2.svg',
          iconAlt: 'icon 2',
          heading: '2 bags of Mitocreamer',
          subheading: '(30 day supply) ($39.99 per bag)',
          description: 'Indulge in creamy, wholesome goodness that fuels both body and spirit.'
        },
        {
          iconSrc: 'assets/images/latte-icon-3.svg',
          iconAlt: 'icon 3',
          heading: '1 free bottle of Everlo Electrolyte Drops',
          subheading: '($26.96 per bottle)',
          description: "Stay hydrated and energized, ensuring you're ready to tackle the day ahead."
        },
        {
          iconSrc: 'assets/images/latte-icon-4.svg',
          iconAlt: 'icon 4',
          heading: '1 free bottle of Vitamin D3/K2 Drops',
          subheading: '($26.96 per bottle)',
          description: "Feel the warmth of the sun in every sip, uplifting your body's health and your mood."
        },
        {
          iconSrc: 'assets/images/latte-icon-5.svg',
          iconAlt: 'icon 5',
          heading: 'Exclusive Bonuses for Early Bird Lifeboosters',
          description: 'The first 300 challengers receive bonus gifts: <span class="font-semibold underline text-brownLatte">our signature frother</span> and Double.'
        }
      ]
    },
    {
      name: 'productImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'svg', 'png', 'webp'],
      defaultValue: 'assets/images/image3-1.png'
    },
    {
      name: 'productImageAlt',
      type: 'text',
      defaultValue: 'coffee products'
    },
    {
      name: 'oldPrice',
      type: 'text',
      defaultValue: '$168.39'
    },
    {
      name: 'newPrice',
      type: 'text',
      defaultValue: '$79.95'
    },
    {
      name: 'discount',
      type: 'text',
      defaultValue: 'Save over 52%'
    },
    {
      name: 'ctaText',
      type: 'richText',
      defaultValue: 'Join the Challenge Today'
    },
    {
      name: 'features',
      type: 'list',
      subFields: [
        {
          name: 'text',
          type: 'richText',
          required: true
        }
      ],
      defaultValue: [
        {
          text: 'Love It or Your Money Back'
        },
        {
          text: 'Discover a new zenith of well-being within 15 days, or get a full refund.'
        },
        {
        text: 'Refill ships in 30 days, 5 Days before your next shipment is set to go through, we will email you a reminder. By default, it will be another 30 servings delivered to your door. You can adjust or cancel at any time. '
        }
      ]
    },
    {
      name: 'products',
      type: 'list',
      subFields: [
        { name: 'id', type: 'string', defaultValue: '41895677100092' },
        { name: 'qty', type: 'number', defaultValue: 1 },
        { name: 'price', type: 'number', defaultValue: 70.95 },
        { name: 'title', type: 'string', defaultValue: 'Latte Bundle' },
        { name: 'selling_plan', type: 'string', defaultValue: '1406730300' },
      ],
      defaultValue: [
        { id: '41895677100092', qty: 1, price: 70.95, title: 'Latte Bundle', selling_plan: '1406730300' },
        { id: '40267005919292', qty: 1, price: 29.95, title: 'EverLo Coffee Drops', selling_plan: null },
        { id: '40282477330492', qty: 1, price: 0, title: 'Lifeboost Frother', selling_plan: null },
      ]
    },
    {
      name: 'productHandle',
      type: 'string',
      defaultValue: 'longevity-latte'
    },
    {
      name: 'sellingPlanName',
      type: 'string',
      defaultValue: '1 month subscription'
    }
  ]
});
</script>

<style>
.bg-glassmorhpism {
  background: rgba(234, 155, 66, 0.23);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(6.6px);
}

</style>
